/**
 * Aides utility (EPIC-005) — helpers métier purs pour le wizard
 * d'éligibilité ACRE/ARE/ARCE (spec §2.G).
 *
 * L'utilisateur saisit lui-même son `acreStatus` via un select binaire
 * (eligible_not_requested | not_eligible) — Notamicro ne reçoit jamais
 * les motifs détaillés (RGPD).
 *
 * 0 dépendance externe — pures fonctions, faciles à tester.
 */

/**
 * Calcule `acre_end_date` à partir de `acre_start_date` (DT3 EPIC-005).
 *
 * Algorithme : trimestre civil contenant la date de début → 4 trimestres
 * plus tard, dernier jour de ce trimestre. Le trimestre de début compte
 * comme un trimestre plein, même partiel.
 *
 * Exemples :
 *   01/01/2026 → 31/03/2027 (T1 → T1+4 = T1 année suivante)
 *   15/03/2026 → 31/03/2027
 *   01/06/2026 → 30/06/2027
 *   31/12/2026 → 31/12/2027
 *   29/02/2028 → 31/03/2029
 *
 * @param {string} startDateIso - YYYY-MM-DD
 * @returns {string|null} - YYYY-MM-DD, ou null si entrée invalide
 */
export function computeAcreEndDate(startDateIso) {
  if (!startDateIso || typeof startDateIso !== "string") return null;
  const m = startDateIso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  // Vérification que la date est valide (rejette 2026-02-30 par ex.).
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year
    || probe.getUTCMonth() !== month - 1
    || probe.getUTCDate() !== day
  ) {
    return null;
  }

  let quarterEndMonth;
  if (month <= 3) quarterEndMonth = 3;
  else if (month <= 6) quarterEndMonth = 6;
  else if (month <= 9) quarterEndMonth = 9;
  else quarterEndMonth = 12;

  let endYear = year + 1;
  let endMonthExclusive = quarterEndMonth + 1;
  if (endMonthExclusive > 12) {
    endMonthExclusive = 1;
    endYear++;
  }
  // 1er du mois suivant - 1 jour = dernier jour du mois précédent.
  const firstOfNext = new Date(Date.UTC(endYear, endMonthExclusive - 1, 1));
  const lastOfQuarter = new Date(firstOfNext.getTime() - 86400000);

  const yyyy = lastOfQuarter.getUTCFullYear();
  const mm = String(lastOfQuarter.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(lastOfQuarter.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Date pivot du barème ACRE (§2.G/§2.H) : 50 % d'exonération avant cette
 * date, 25 % à partir. Le taux est figé sur la date de début d'activité.
 */
export const ACRE_RATE_PIVOT = "2026-07-01";

/**
 * Libellé du taux d'exonération ACRE applicable à une date de début donnée.
 * 50 % avant le 01/07/2026, 25 % à partir (§2.G/§2.H). Le taux est figé
 * sur `acre_start_date`. Sans date valide, on affiche le taux en vigueur au
 * démarrage actuel (50 %) — mis à jour live dès qu'une date est saisie.
 *
 * @param {string} startDateIso - YYYY-MM-DD
 * @returns {string} "50 %" ou "25 %"
 */
export function acreRateLabel(startDateIso) {
  if (
    typeof startDateIso === "string"
    && /^\d{4}-\d{2}-\d{2}$/.test(startDateIso)
  ) {
    return startDateIso >= ACRE_RATE_PIVOT ? "25 %" : "50 %";
  }
  return "50 %";
}

/**
 * Renvoie le numéro du trimestre (1-4) pour un mois donné.
 *
 * @param {number} month - 1..12
 * @returns {number} 1..4
 */
export function getQuarter(month) {
  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;
  return 4;
}

/**
 * Libellé FR du trimestre civil contenant la date donnée.
 * Ex. 2026-06-01 → "T2 2026 (avril → juin)".
 *
 * @param {string} startDateIso - YYYY-MM-DD
 * @returns {string} libellé, ou "—" si entrée invalide
 */
export function formatQuarter(startDateIso) {
  if (!startDateIso || typeof startDateIso !== "string") return "—";
  const m = startDateIso.match(/^(\d{4})-(\d{2})-/);
  if (!m) return "—";
  const year = m[1];
  const month = parseInt(m[2], 10);
  const q = getQuarter(month);
  const ranges = {
    1: "janvier → mars",
    2: "avril → juin",
    3: "juillet → septembre",
    4: "octobre → décembre",
  };
  return `T${q} ${year} (${ranges[q]})`;
}

/**
 * Validation purement client. Retourne un objet `{ field: message }`,
 * vide si le payload est OK. Le serveur ré-applique sa propre
 * validation — celle-ci est juste là pour éviter un round-trip 422.
 *
 * @param {object} payload
 * @returns {Object<string, string>}
 */
export function validatePayload(payload) {
  const errors = {};

  // statut ACRE : doit valoir une des deux constantes serveur.
  const status = payload.acreStatus;
  if (status !== "eligible_not_requested" && status !== "not_eligible") {
    errors.acreStatus = "Sélectionnez votre éligibilité ACRE.";
  }

  // date début optionnelle, format ISO.
  if (payload.acreStartDate) {
    if (
      typeof payload.acreStartDate !== "string"
      || !/^\d{4}-\d{2}-\d{2}$/.test(payload.acreStartDate)
      || computeAcreEndDate(payload.acreStartDate) === null
    ) {
      errors.acreStartDate = "Date invalide.";
    }
  }

  // type chômage obligatoire.
  const type = payload.unemploymentBenefitType;
  if (type !== "none" && type !== "are" && type !== "arce") {
    errors.unemploymentBenefitType = "Sélection requise.";
  }

  if (type === "are") {
    if (!isPositiveDecimal(payload.areDailyNetAmount)) {
      errors.areDailyNetAmount = "Montant journalier requis (> 0).";
    }
    if (
      typeof payload.areRightsOpeningDate !== "string"
      || !/^\d{4}-\d{2}-\d{2}$/.test(payload.areRightsOpeningDate)
    ) {
      errors.areRightsOpeningDate = "Date d'ouverture des droits requise.";
    }
    if (!isPositiveInt(payload.areTotalDaysGranted)) {
      errors.areTotalDaysGranted = "Durée totale (jours) requise (> 0).";
    }
    if (
      payload.areRemainingDaysAtMicroStart != null
      && !isNonNegativeInt(payload.areRemainingDaysAtMicroStart)
    ) {
      errors.areRemainingDaysAtMicroStart = "Reliquat doit être ≥ 0.";
    }
    if (
      isPositiveInt(payload.areTotalDaysGranted)
      && payload.areRemainingDaysAtMicroStart != null
      && isNonNegativeInt(payload.areRemainingDaysAtMicroStart)
      && Number(payload.areRemainingDaysAtMicroStart) > Number(payload.areTotalDaysGranted)
    ) {
      errors.areRemainingDaysAtMicroStart = "Reliquat ne peut pas excéder la durée totale.";
    }
  }

  if (type === "arce") {
    if (!isPositiveDecimal(payload.arceTotalAmount)) {
      errors.arceTotalAmount = "Montant total ARCE requis (> 0).";
    }
    if (
      typeof payload.arceFirstPaymentDate !== "string"
      || !/^\d{4}-\d{2}-\d{2}$/.test(payload.arceFirstPaymentDate)
    ) {
      errors.arceFirstPaymentDate = "Date de la 1ère tranche requise.";
    }
    if (!isPositiveDecimal(payload.arceFirstPaymentAmount)) {
      errors.arceFirstPaymentAmount = "Montant de la 1ère tranche requis (> 0).";
    }
  }

  return errors;
}

function isPositiveDecimal(raw) {
  if (raw == null || raw === "") return false;
  const n = typeof raw === "string" ? Number(raw.replace(",", ".")) : Number(raw);
  return Number.isFinite(n) && n > 0;
}

function isPositiveInt(raw) {
  if (raw == null || raw === "") return false;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0;
}

function isNonNegativeInt(raw) {
  if (raw == null || raw === "") return false;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0;
}
